import React from 'react'
import type { CreatorTier } from '../../api/creatorSubscriptions'

type Props = {
  open: boolean
  onClose: () => void
  creatorName?: string
  monthlyPricePixe?: number | null
  tiers?: CreatorTier[] | null
  features?: Record<string, boolean> | null
  intent?: 'subscribe' | 'upgrade'
  receivePostsEmail?: boolean
  onChangeReceivePostsEmail?: (next: boolean) => void
  onConfirm: (tierId?: number | null, opts?: { receivePostsEmail?: boolean }) => Promise<void>
  busy?: boolean
  error?: string | null
}

function featureLabel(key: string) {
  const map: Record<string, string> = {
    enable_subscriber_comments: 'Subscriber comments',
    enable_early_access: 'Early access',
    enable_subscriber_badge: 'Subscriber badge',
    enable_polls: 'Polls',
    enable_bts_pixies: 'Behind-the-scenes Pixes',
  }
  return map[key] || key
}

export default function SubscribeModal({
  open,
  onClose,
  creatorName,
  monthlyPricePixe,
  tiers,
  features,
  intent = 'subscribe',
  receivePostsEmail = false,
  onChangeReceivePostsEmail,
  onConfirm,
  busy,
  error,
}: Props) {
  if (!open) return null
  const tierList = (tiers || []).filter(Boolean).slice().sort((a, b) => (a.rank || 0) - (b.rank || 0))
  const [selectedTierId, setSelectedTierId] = React.useState<number | null>(tierList[0]?.id ?? null)
  React.useEffect(() => {
    setSelectedTierId(tierList[0]?.id ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tierList.map((t) => t.id).join(',')])

  const enabled = Object.entries(features || {}).filter(([, v]) => Boolean(v)).map(([k]) => featureLabel(k))
  const title = intent === 'upgrade' ? 'Upgrade tier' : 'Subscribe'
  const primaryLabel = intent === 'upgrade' ? 'Upgrade' : 'Confirm'
  const selectedTier = tierList.find((t) => t.id === selectedTierId) || null
  const canConfirm = tierList.length > 0 ? Boolean(selectedTierId) : Boolean(monthlyPricePixe)
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-5 shadow-xl">
        <div className="text-lg font-extrabold">{title}</div>
        <div className="mt-1 text-sm text-white/75">
          {creatorName ? `Support ${creatorName} and unlock all subscriber Pixes.` : 'Unlock subscriber Pixes.'}
        </div>
        {tierList.length > 0 ? (
          <div className="mt-4 space-y-2">
            <div className="text-sm text-white/70">Choose a tier</div>
            {tierList.map((t) => {
              const active = t.id === selectedTierId
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTierId(t.id)}
                  className={[
                    'w-full text-left rounded-xl border p-3 transition',
                    active ? 'border-white/20 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white/90">{t.name}</div>
                      <div className="mt-0.5 text-xs text-white/65">Tier {t.rank}+</div>
                    </div>
                    <div className="text-sm font-extrabold text-white">{t.monthly_price_pixe} PIXE</div>
                  </div>
                </button>
              )
            })}
            {enabled.length > 0 && (
              <ul className="mt-2 text-sm text-white/80 list-disc pl-5">
                {enabled.slice(0, 5).map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm text-white/70">Monthly</div>
            <div className="text-2xl font-extrabold">{monthlyPricePixe ? `${monthlyPricePixe} PIXE` : 'PIXE'}</div>
            {enabled.length > 0 && (
              <ul className="mt-3 text-sm text-white/80 list-disc pl-5">
                {enabled.slice(0, 5).map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {error ? <div className="mt-3 text-sm text-red-400">{error}</div> : null}

        {onChangeReceivePostsEmail ? (
          <label className="mt-4 flex items-start gap-2 text-xs text-white/75">
            <input
              type="checkbox"
              checked={receivePostsEmail}
              onChange={(e) => onChangeReceivePostsEmail(Boolean(e.target.checked))}
              className="mt-0.5"
            />
            <span>Email me new subscriber Pixes.</span>
          </label>
        ) : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
            disabled={Boolean(busy)}
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => onConfirm(selectedTier?.id ?? null, { receivePostsEmail })}
            className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            disabled={Boolean(busy) || !canConfirm}
          >
            {busy ? 'Working…' : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
